import React, { useMemo } from 'react'
import { SMBeamLoad, LoadCategory } from '../../types'
import { useTranslation, TranslationKey } from '@structura/i18n'

type TFunc = ReturnType<typeof useTranslation>['t']

interface AppliedLoadsListProps
{
  selectedSpan: number
  loads: SMBeamLoad[]
  onRemoveLoad: (loadId: string, spanId: number) => void
  onEditLoad?: (load: SMBeamLoad) => void
  editingLoadId?: string | null
}

// Load category → short badge label.
const CATEGORY_BADGE_KEY: Record<LoadCategory, TranslationKey> = {
  Permanent: 'beam.loads.badgePermanent',
  Variable: 'beam.loads.badgeVariable',
  Snow: 'beam.loads.badgeSnow',
  Wind: 'beam.loads.badgeWind',
  Prestress: 'beam.loads.badgePrestress'
}

// Load type → short readable name shown in the applied-loads list.
const LOAD_TYPE_KEY: Partial<Record<SMBeamLoad['type'], TranslationKey>> = {
  UniformLoad: 'beam.loads.shortUniform',
  PointLoad: 'beam.loads.shortPoint',
  TriangularRight: 'beam.loads.shortTriRight',
  TriangularLeft: 'beam.loads.shortTriLeft',
  AxialForce: 'beam.loads.shortAxial'
}

function triangularDirectionKey(start: number, end: number): TranslationKey
{
  const hasUpward = start > 0 || end > 0
  const hasDownward = start < 0 || end < 0
  if (hasUpward && hasDownward) return 'beam.loads.dirUpDown'
  return hasUpward ? 'beam.loads.dirUp' : 'beam.loads.dirDown'
}

function buildLoadDescription(load: SMBeamLoad, t: TFunc): string
{
  switch (load.type)
  {
    case 'UniformLoad':
      return t('beam.loads.uniformDesc', { intensity: load.uniformIntensity.toFixed(4), dir: t(load.uniformIntensity < 0 ? 'beam.loads.dirDown' : 'beam.loads.dirUp') })
    case 'PointLoad':
      return t('beam.loads.pointDesc', { load: load.pointLoad, pos: load.pointLoadPosition, dir: t(load.pointLoad < 0 ? 'beam.loads.dirDown' : 'beam.loads.dirUp') })
    case 'TriangularRight':
    case 'TriangularLeft':
    {
      const start = load.startIntensity ?? 0
      const end = load.endIntensity ?? 0
      return t('beam.loads.triangularDesc', { start, end, startPos: load.startPosition, endPos: load.endPosition, dir: t(triangularDirectionKey(start, end)) })
    }
    case 'AxialForce':
    {
      const ecc = Math.abs(load.normalForceEccentricity) > 0.001
        ? t('beam.loads.axialEcc', { e: load.normalForceEccentricity, m: (load.normalForce * load.normalForceEccentricity).toFixed(2) })
        : ''
      return t('beam.loads.axialDesc', { force: load.normalForce, end: t(load.axialForceEnd === 'left' ? 'beam.loads.endLeft' : 'beam.loads.endRight'), dir: t(load.normalForce > 0 ? 'beam.loads.tension' : 'beam.loads.compression'), ecc })
    }
    default:
      return ''
  }
}

const AppliedLoadsList: React.FC<AppliedLoadsListProps> = ({ selectedSpan, loads, onRemoveLoad, onEditLoad, editingLoadId }) =>
{
  const { t } = useTranslation()
  const content = useMemo(() => {
    if (!loads || loads.length === 0)
    {
      return <p className="text-muted">{t('beam.loads.noLoads')}</p>
    }

    return loads.map(load => {
      const isSelfWeight = load.id.includes('selfweight')
      const isEditing = editingLoadId === load.id
      // Remove by the load's own span id (works whether one span or "all spans" is selected).
      const handleRemove = () => onRemoveLoad(load.id, load.spanId)
      const handleEdit = () => onEditLoad?.(load)

      return (
        <div
          key={load.id}
          className={`d-flex justify-content-between align-items-center mb-2 p-2 rounded ${isEditing ? 'border border-primary bg-primary bg-opacity-10' : isSelfWeight ? 'bg-warning bg-opacity-25' : 'bg-light'}`}
        >
          <div>
            {isSelfWeight && (
              <i className="bi bi-info-circle me-1 text-warning" title={t('beam.loads.selfWeightAutoTitle')}></i>
            )}
            <strong>{isSelfWeight ? t('beam.materials.selfWeight') : t(LOAD_TYPE_KEY[load.type] ?? 'beam.loads.shortUniform')}</strong>
            <span className="badge bg-secondary ms-2">{t(CATEGORY_BADGE_KEY[load.category || 'Variable'])}</span>
            <span className="ms-2">{buildLoadDescription(load, t)}</span>
            {isSelfWeight && (
              <small className="text-muted d-block">{t('beam.loads.selfWeightUpdates')}</small>
            )}
          </div>
          <div className="d-flex gap-1">
            {!isSelfWeight && (
              <button
                className={`btn btn-sm ${isEditing ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={handleEdit}
                title={t('beam.loads.editLoad')}
                type="button"
              >
                <i className="bi bi-pencil"></i>
              </button>
            )}
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={handleRemove}
              title={isSelfWeight ? t('beam.loads.removeSelfWeight') : t('beam.loads.removeLoad')}
              type="button"
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        </div>
      )
    })
  }, [loads, onRemoveLoad, onEditLoad, editingLoadId, selectedSpan, t])

  return (
    <div>
      <label className="form-label fw-bold">
        {selectedSpan === 0 ? t('beam.loads.appliedLoadsAll') : t('beam.loads.appliedLoads', { span: selectedSpan })}
      </label>
      {content}
    </div>
  )
}

export default AppliedLoadsList


